// ── Zone Activity page controller ─────────────────────────────────────────
// Owns page state, control wiring, the card grid render pass and the detail
// modal. The live occupancy layer runs independently (zone-activity-live.ts)
// and only ever patches cards in place.

import { expandGroups } from '../api/group-hierarchy';
import { escapeHtml } from '../utils/html';
import { formatInSessionTz, resetSessionTimeZone, sessionTimeZoneLabel } from '../utils/timezone';
import { state } from '../state';
import { loadZoneActivityPageData, resetZoneActivityData } from './zone-activity-data';
import { ZONE_TYPE_FILTER_UNTYPED, zoneTypeFilterValue } from './zone-activity-data';
import type { ZoneActivityPageData, ZoneTypeOption } from './zone-activity-data';
import {
  discoverDriverDevices, maxRangeDaysFor, runZoneAnalysis, SINGLE_ENTITY_MAX_DAYS,
} from './zone-activity-analysis';
import {
  getLiveOccupancy, refreshLiveOccupancy, resetZoneActivityLive,
  setLiveScope, setOnLiveUpdate, startZoneActivityRefresh, stopZoneActivityRefresh,
} from './zone-activity-live';
import {
  buildEntityZoneRows, buildHeatMap, cardHtml, liveSignature, patchCardLive, plateSvg,
  renderEntityResults, renderZoneDetail, showZoneActivityState, stippleSprite,
} from './zone-activity-results';
import type { DetailSort, EntitySort, EntityZoneRow, VisitSort } from './zone-activity-results';
import type { LiveOccupant, PreparedZone, ZoneAnalysisResult } from './zone-activity-types';

export { startZoneActivityRefresh, stopZoneActivityRefresh };

/** Above this many zones the plates are hydrated on approach rather than up front. */
const LAZY_PLATE_THRESHOLD = 800;

const el = (id: string): HTMLElement | null => document.getElementById(id);
const noOccupants: LiveOccupant[] = [];

let _data: ZoneActivityPageData | null = null;
let _result: ZoneAnalysisResult | null = null;
let _initialized = false;
let _loading = false;
let _running = false;
let _lazyPlates = false;
let _plateObserver: IntersectionObserver | null = null;

let _search = '';
let _typeFilter = '';
let _sort: 'live' | 'visits' | 'dwell' | 'name' = 'live';
let _activeOnly = false;

let _detailZoneId: string | null = null;
let _detailSort: DetailSort = { col: 'visits', asc: false };
/** Shared by every expanded entry/exit list, in both the zone-detail modal and the entity view. */
let _innerSort: VisitSort = { col: 'entry', asc: false };

// Which rows are expanded survives a re-render (e.g. clicking a sort header rebuilds the
// table's HTML from scratch) — otherwise sorting collapses whatever the user had open.
const _expandedVehicleRows = new Set<string>(); // zone detail modal, keyed by deviceId
const _expandedZoneRows    = new Set<string>(); // entity view, keyed by zoneId

function restoreExpanded(root: HTMLElement, expanded: Set<string>, rowAttr: string, detailAttr: string): void {
  if (!expanded.size) return;
  root.querySelectorAll<HTMLElement>(`.za-vrow[${rowAttr}]`).forEach(row => {
    const key = row.getAttribute(rowAttr);
    if (!key || !expanded.has(key)) return;
    const detail = root.querySelector<HTMLElement>(`.za-visits-row[${detailAttr}="${CSS.escape(key)}"]`);
    if (detail) { detail.classList.remove('hidden'); row.classList.add('is-open'); }
  });
}

/** zoneId → last rendered live signature, so unchanged cards do zero DOM work. */
const _sigs = new Map<string, string>();
/** zoneId → its slot element in the current render pass. */
const _slots = new Map<string, HTMLElement>();

// ── Scope: the fleet/group grid ("By Zone"), or one vehicle/driver's own
// history ("By Vehicle" / "By Driver"). runZoneAnalysis itself never changes
// between these — it always just takes a device id list; "By Vehicle" passes
// a single id, "By Driver" first discovers which devices that driver actually
// drove, then filters the SAME per-visit driverId attribution the engine
// already computes down to just that person.
type ScopeMode = 'group' | 'vehicle' | 'driver';
let _scopeMode: ScopeMode = 'group';
let _focusDeviceId: string | null = null;
let _focusDeviceName: string | null = null;
let _focusDriverId: string | null = null;
let _focusDriverName: string | null = null;
let _driverDiscoveryTruncated = false;
let _entityRows: EntityZoneRow[] = [];
let _entitySort: EntitySort = { col: 'visits', asc: false };

// ── Date range ────────────────────────────────────────────────────────────

/** Local-time formatting: toISOString() would shift the day for negative UTC offsets. */
function fmtDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function zaSetRange(range: string): void {
  const now     = new Date();
  const toLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const from    = new Date(toLocal);
  if (range === '7d')  from.setDate(from.getDate() - 6);
  if (range === '14d') from.setDate(from.getDate() - 13);
  if (range === '31d') from.setDate(from.getDate() - 30);
  (el('zaFrom') as HTMLInputElement).value = fmtDateInput(from);
  (el('zaTo')   as HTMLInputElement).value = fmtDateInput(toLocal);
  updateRangeCap();
}

/** Devices left after the group filter — the scope for both live and analysis. */
function scopedDeviceIds(): string[] {
  if (!_data) return [];
  const selected = getSelectedGroupIds();
  if (!selected.size || selected.size === _data.groups.length) return _data.devices.map(d => d.id);
  const expanded = expandGroups(selected, _data.groupChildren);
  return _data.devices.filter(d => d.groupIds.some(g => expanded.has(g))).map(d => d.id);
}

/**
 * The max analysable range for the current scope. Group mode tracks a
 * vehicle-day budget (full accuracy means downloading every scoped vehicle's
 * breadcrumbs); a single vehicle or driver touches a small, range-bounded
 * slice of data regardless of fleet size, so it gets a flat, generous ceiling.
 */
function currentMaxDays(): number {
  return _scopeMode === 'group' ? maxRangeDaysFor(scopedDeviceIds().length) : SINGLE_ENTITY_MAX_DAYS;
}

/** Clamp the range to what the current scope can be analysed over, and explain the ceiling. */
function updateRangeCap(): void {
  const note = el('zaRangeCapNote');
  if (!note || !_data) return;

  const maxDays = currentMaxDays();
  const fromEl  = el('zaFrom') as HTMLInputElement;
  const toEl    = el('zaTo')   as HTMLInputElement;
  if (!fromEl.value || !toEl.value) return;

  const from = new Date(fromEl.value + 'T00:00:00');
  const to   = new Date(toEl.value + 'T23:59:59');
  const days = Math.ceil((to.getTime() - from.getTime()) / 86_400_000);

  let clamped = false;
  if (days > maxDays) {
    const newFrom = new Date(to.getTime() - (maxDays - 1) * 86_400_000);
    fromEl.value = fmtDateInput(newFrom);
    clamped = true;
  }

  const scopeLabel = _scopeMode === 'group'
    ? `${scopedDeviceIds().length.toLocaleString()} vehicle${scopedDeviceIds().length === 1 ? '' : 's'} in scope`
    : _scopeMode === 'vehicle' ? 'a single vehicle' : 'a single driver';
  note.textContent = `Max range ${maxDays} days for ${scopeLabel}` +
    (clamped ? ' — dates adjusted. Narrow the vehicle group for a longer range.' : '.');
  note.classList.toggle('is-clamped', clamped);
}

// ── Group selector (same markup/behaviour as the Safety page) ─────────────

function getSelectedGroupIds(): Set<string> {
  const out = new Set<string>();
  el('zaGselectorList')
    ?.querySelectorAll<HTMLInputElement>('input[type=checkbox]:checked')
    .forEach(cb => out.add(cb.value));
  return out;
}

function updateGroupLabel(): void {
  const label = el('zaGselectorLabel');
  if (!label || !_data) return;
  const total    = _data.groups.length;
  const selected = getSelectedGroupIds().size;
  label.textContent = !total
    ? 'All Vehicles'
    : selected === total ? `All Groups (${total})` : `${selected} / ${total} groups`;
}

function populateGroupSelector(): void {
  const list = el('zaGselectorList');
  if (!list || !_data) return;
  list.innerHTML = '';
  _data.groups.forEach(({ id, name, depth }) => {
    const lbl = document.createElement('label');
    lbl.className = 'vselector-item';
    if (depth > 0) lbl.style.paddingLeft = `${0.75 + depth * 0.85}rem`;
    lbl.innerHTML = `<input type="checkbox" value="${escapeHtml(id)}" checked><span class="vselector-item-name">${escapeHtml(name)}</span>`;
    lbl.querySelector('input')!.addEventListener('change', onScopeChanged);
    list.appendChild(lbl);
  });
  updateGroupLabel();
}

function onScopeChanged(): void {
  updateGroupLabel();
  updateRangeCap();
  const ids = scopedDeviceIds();
  setLiveScope(_data && ids.length === _data.devices.length ? null : new Set(ids));
}

// ── Scope mode: By Zone / By Vehicle / By Driver ───────────────────────────

/** The export button is shown whenever the CURRENT view has something to export. */
function updateExportVisibility(): void {
  const has = _scopeMode === 'group' ? !!_result : _entityRows.length > 0;
  (el('zaExportBtn') as HTMLElement | null)?.style.setProperty('display', has ? '' : 'none');
}

function setScopeMode(mode: ScopeMode): void {
  _scopeMode = mode;
  document.querySelectorAll<HTMLElement>('.za-scope-btn')
    .forEach(b => b.classList.toggle('active', b.dataset.scope === mode));
  el('zaGroupScopeWrap')  ?.classList.toggle('hidden', mode !== 'group');
  el('zaVehicleScopeWrap')?.classList.toggle('hidden', mode !== 'vehicle');
  el('zaDriverScopeWrap') ?.classList.toggle('hidden', mode !== 'driver');
  el('zaZoneView')  ?.classList.toggle('hidden', mode !== 'group');
  el('zaEntityView')?.classList.toggle('hidden', mode === 'group');
  updateExportVisibility();

  if (mode === 'group') {
    onScopeChanged();
  } else {
    // Vehicle/driver mode always reads live data unrestricted by the group
    // filter — a fleet manager picking a specific truck shouldn't have its
    // live status silently hidden because that truck's group happens to be
    // unchecked elsewhere on the page.
    setLiveScope(null);
    renderEntityView();
    renderEntityLiveCallout();
  }
  updateRangeCap();
}

function populateVehiclePicker(): void {
  const list = el('zaVehiclePickerList');
  if (!list || !_data) return;
  list.innerHTML = '';
  [..._data.devices].sort((a, b) => a.name.localeCompare(b.name)).forEach(d => {
    const item = document.createElement('div');
    item.className = 'vselector-item vselector-item-single';
    item.dataset.id = d.id;
    item.innerHTML = `<span class="vselector-item-name">${escapeHtml(d.name)}</span>`;
    item.addEventListener('click', () => selectFocusVehicle(d.id, d.name));
    list.appendChild(item);
  });
}

function populateDriverPicker(): void {
  const list = el('zaDriverPickerList');
  if (!list || !_data) return;
  list.innerHTML = '';

  // Only users flagged as drivers in MyGeotab. If a database flags nobody,
  // fall back to every user rather than render an empty, unusable picker —
  // an unfiltered list is a nuisance, but no list at all blocks the feature.
  const people = _data.drivers.length
    ? _data.drivers
    : [..._data.userMap.entries()].map(([id, name]) => ({ id, name }))
        .sort((a, b) => a.name.localeCompare(b.name));

  people.forEach(({ id, name }) => {
    const item = document.createElement('div');
    item.className = 'vselector-item vselector-item-single';
    item.dataset.id = id;
    item.innerHTML = `<span class="vselector-item-name">${escapeHtml(name)}</span>`;
    item.addEventListener('click', () => selectFocusDriver(id, name));
    list.appendChild(item);
  });
}

function selectFocusVehicle(id: string, name: string): void {
  _focusDeviceId = id;
  _focusDeviceName = name;
  el('zaVehiclePickerLabel')!.textContent = name;
  el('zaVehiclePickerList')?.querySelectorAll<HTMLElement>('.vselector-item-single')
    .forEach(n => n.classList.toggle('selected', n.dataset.id === id));
  el('zaVehiclePickerPanel')?.classList.add('hidden');
  updateRangeCap();
  renderEntityView();
  renderEntityLiveCallout();
}

function selectFocusDriver(id: string, name: string): void {
  _focusDriverId = id;
  _focusDriverName = name;
  el('zaDriverPickerLabel')!.textContent = name;
  el('zaDriverPickerList')?.querySelectorAll<HTMLElement>('.vselector-item-single')
    .forEach(n => n.classList.toggle('selected', n.dataset.id === id));
  el('zaDriverPickerPanel')?.classList.add('hidden');
  updateRangeCap();
  renderEntityView();
  renderEntityLiveCallout();
}

/** "Currently inside" callout for the focused vehicle/driver — live, independent of any run. */
function renderEntityLiveCallout(): void {
  const box = el('zaEntityLive');
  if (!box || !_data) return;
  if (_scopeMode === 'group') { box.classList.add('hidden'); return; }

  const focusId = _scopeMode === 'vehicle' ? _focusDeviceId : _focusDriverId;
  if (!focusId) { box.classList.add('hidden'); return; }

  const live = getLiveOccupancy();
  if (!live) { box.classList.add('hidden'); return; }

  const zoneById = new Map(_data.zones.map(z => [z.id, z]));
  const matches: string[] = [];
  live.byZone.forEach((occupants, zoneId) => {
    occupants.forEach(o => {
      const isMatch = _scopeMode === 'vehicle' ? o.deviceId === focusId : o.driverId === focusId;
      if (!isMatch) return;
      const zoneName = escapeHtml(zoneById.get(zoneId)?.name ?? zoneId);
      matches.push(_scopeMode === 'driver'
        ? `<span class="za-live-chip"><span class="za-pulse"></span> Inside <strong>${zoneName}</strong> · ${escapeHtml(o.deviceName)}</span>`
        : `<span class="za-live-chip"><span class="za-pulse"></span> Inside <strong>${zoneName}</strong></span>`);
    });
  });

  box.classList.remove('hidden');
  box.innerHTML = matches.length
    ? matches.join('')
    : '<span class="za-live-chip za-live-chip-off">Not currently inside any zone</span>';
}

/** Renders the entity header + zone table/placeholder for the focused vehicle/driver. */
function renderEntityView(): void {
  const header = el('zaEntityHeader');
  const body   = el('zaEntityBody');
  if (!header || !body) return;

  const isVehicle = _scopeMode === 'vehicle';
  const focusName = isVehicle ? _focusDeviceName : _focusDriverName;

  if (!focusName) {
    header.innerHTML = '';
    body.innerHTML = `<div class="za-detail-hint">Choose a ${isVehicle ? 'vehicle' : 'driver'} above, then click <strong style="color:var(--text);">Run Analysis</strong>.</div>`;
    return;
  }

  header.innerHTML = `<div class="za-entity-kicker">${isVehicle ? 'Vehicle' : 'Driver'}</div><div class="za-entity-name">${escapeHtml(focusName)}</div>`;

  if (!_result) {
    body.innerHTML = `<div class="za-detail-hint">Click <strong style="color:var(--text);">Run Analysis</strong> to see every zone ${escapeHtml(focusName)} visited in the selected range.</div>`;
    return;
  }

  const warnHtml = _scopeMode === 'driver' && _driverDiscoveryTruncated
    ? '<div class="za-warn" style="margin-bottom:0.75rem;"><strong>This driver drove more vehicles than could be analysed at once</strong> — results cover their most-driven vehicles in this range; a few less-used ones may be missing.</div>'
    : '';
  body.innerHTML = warnHtml + '<div id="zaEntityResultsInner"></div>';
  renderEntityResults('zaEntityResultsInner', _entityRows, _data!, _entitySort, _innerSort);
  restoreExpanded(body, _expandedZoneRows, 'data-ezone', 'data-evisits');
}

// ── Card grid ─────────────────────────────────────────────────────────────

function visibleZones(): PreparedZone[] {
  if (!_data) return [];
  const live = getLiveOccupancy();
  const q = _search.trim().toLowerCase();

  let zones = _data.zones;
  if (q) zones = zones.filter(z => z.name.toLowerCase().includes(q));
  if (_typeFilter === ZONE_TYPE_FILTER_UNTYPED) {
    zones = zones.filter(z => !z.typeNames.length);
  } else if (_typeFilter.startsWith('t:')) {
    const wanted = _typeFilter.slice(2);
    zones = zones.filter(z => z.typeNames.includes(wanted));
  }
  if (_activeOnly) {
    zones = zones.filter(z =>
      (live?.byZone.get(z.id)?.length ?? 0) > 0 || (_result?.byZone.get(z.id)?.visitCount ?? 0) > 0);
  }

  const liveCount = (z: PreparedZone): number => live?.byZone.get(z.id)?.length ?? 0;
  const agg = (z: PreparedZone) => _result?.byZone.get(z.id);

  const sorted = [...zones];
  sorted.sort((a, b) => {
    switch (_sort) {
      case 'visits': return (agg(b)?.visitCount ?? 0) - (agg(a)?.visitCount ?? 0) || a.name.localeCompare(b.name);
      case 'dwell':  return (agg(b)?.totalDwellMs ?? 0) - (agg(a)?.totalDwellMs ?? 0) || a.name.localeCompare(b.name);
      case 'name':   return a.name.localeCompare(b.name);
      default:       return liveCount(b) - liveCount(a) || a.name.localeCompare(b.name);
    }
  });
  return sorted;
}

function renderGrid(): void {
  const grid = el('zaCardGrid');
  if (!grid || !_data) return;

  const zones = visibleZones();
  const live  = getLiveOccupancy();
  const heat  = buildHeatMap(_result);

  _sigs.clear();
  _slots.clear();
  disconnectPlateObserver();

  el('zaNoMatch')?.classList.toggle('hidden', zones.length > 0);
  el('zaCount')!.textContent = `${zones.length.toLocaleString()} of ${_data.zones.length.toLocaleString()} zones`;

  _lazyPlates = _data.zones.length > LAZY_PLATE_THRESHOLD;

  const html = zones.map((zone, i) => {
    const occupants = live?.byZone.get(zone.id) ?? noOccupants;
    _sigs.set(zone.id, liveSignature(occupants));
    return cardHtml({
      zone,
      index: i + 1,
      occupants,
      agg: _result?.byZone.get(zone.id),
      heat: heat.get(zone.id) ?? 0,
    });
  }).join('');

  grid.innerHTML = stippleSprite() + html;

  grid.querySelectorAll<HTMLElement>('.za-slot').forEach((slot, i) => {
    const id = slot.dataset.id;
    if (id) _slots.set(id, slot);
    // Roving tabindex: one stop for the whole grid, arrows move between cards.
    slot.tabIndex = i === 0 ? 0 : -1;
  });

  if (_lazyPlates) {
    // Strip the plates back out and refill them as cards approach the viewport,
    // so a database with thousands of zones doesn't parse thousands of SVGs.
    grid.querySelectorAll<HTMLElement>('.za-plate').forEach(p => { p.innerHTML = ''; });
    setupPlateObserver(grid);
  }

  updateLiveMeta();
}

function setupPlateObserver(grid: HTMLElement): void {
  const zoneById = new Map(_data!.zones.map(z => [z.id, z]));
  _plateObserver = new IntersectionObserver((entries, obs) => {
    const live = getLiveOccupancy();
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const slot = entry.target as HTMLElement;
      const zone = slot.dataset.id ? zoneById.get(slot.dataset.id) : undefined;
      const holder = slot.querySelector<HTMLElement>('.za-plate');
      if (zone && holder && !holder.childElementCount) {
        holder.innerHTML = plateSvg(zone, live?.byZone.get(zone.id) ?? noOccupants);
      }
      obs.unobserve(slot);
    }
  }, { rootMargin: '400px' });
  grid.querySelectorAll<HTMLElement>('.za-slot').forEach(s => _plateObserver!.observe(s));
}

function disconnectPlateObserver(): void {
  _plateObserver?.disconnect();
  _plateObserver = null;
}

function updateLiveMeta(): void {
  const live = getLiveOccupancy();
  const meta = el('zaLiveMeta');
  if (!meta) return;
  if (!live) { meta.textContent = 'Live · waiting'; return; }
  let inside = 0;
  live.byZone.forEach(list => { inside += list.length; });
  const updated = formatInSessionTz(live.updatedAtMs, {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  meta.textContent = `Live · ${inside} inside · updated ${updated} ${sessionTimeZoneLabel()}`.trim();
}

/** Patch only the cards whose occupancy actually changed. */
function applyLiveUpdate(): void {
  const live = getLiveOccupancy();
  if (!live || !_data) return;

  if (_scopeMode !== 'group') {
    renderEntityLiveCallout();
    return;
  }

  if (_sort === 'live' || _activeOnly) {
    // Ordering/membership depends on live counts, so a full pass is required.
    renderGrid();
  } else {
    const zoneById = new Map(_data.zones.map(z => [z.id, z]));
    _slots.forEach((slot, zoneId) => {
      const occupants = live.byZone.get(zoneId) ?? noOccupants;
      const sig = liveSignature(occupants);
      if (_sigs.get(zoneId) === sig) return;
      _sigs.set(zoneId, sig);
      const zone = zoneById.get(zoneId);
      if (zone) patchCardLive(slot, zone, occupants);
    });
    updateLiveMeta();
  }

  if (_detailZoneId) openDetail(_detailZoneId, false);
}

function updateMeta(): void {
  const meta = el('zaMeta');
  if (!meta || !_data) return;
  const bits = [`${_data.zones.length.toLocaleString()} active zones`];
  if (_data.ruleBackedZoneCount > 0) {
    bits.push(`${_data.ruleBackedZoneCount.toLocaleString()} rule-verified`);
  }
  if (_result) {
    const dateOpts: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };
    const from = formatInSessionTz(_result.fromMs, dateOpts);
    const to   = formatInSessionTz(_result.toMs, dateOpts);
    let visits = 0;
    _result.byZone.forEach(a => { visits += a.visitCount; });
    bits.push(`${visits.toLocaleString()} visits · ${from} → ${to} · ${_result.scopedDeviceCount.toLocaleString()} vehicles`);
    // Which clock every time on this page is on — the profile zone, not the browser's.
    const tzLabel = sessionTimeZoneLabel(_result.toMs);
    if (tzLabel) bits.push(`times in ${tzLabel}`);
  } else {
    bits.push('Live occupancy only — run an analysis for visit history');
  }
  meta.textContent = bits.join(' · ');
}

function updateWarnBanner(): void {
  const banner = el('zaWarnBanner');
  if (!banner || !_data) return;
  const notes: string[] = [];

  if (_data.zonesTruncated) notes.push('<strong>Zone list truncated</strong> — this database has more zones than can be loaded at once.');
  if (_data.devicesTruncated) notes.push('<strong>Vehicle list truncated</strong> — this database has more devices than can be loaded at once, so some active vehicles may be missing from both live occupancy and visit history.');
  if (_data.skippedZoneCount) notes.push(`<strong>${_data.skippedZoneCount}</strong> zone(s) skipped: their boundary has fewer than 3 points.`);
  if (_result?.tripWindowFailures) {
    notes.push(`<strong>${_result.tripWindowFailures} trip window(s) could not be fetched</strong> even after retrying — some estimated (non-verified) zones' visits in that part of the range may be understated.`);
  }
  if (_result?.eventWindowFailures) {
    notes.push(`<strong>${_result.eventWindowFailures} verified-zone fetch(es) could not be completed</strong> even after retrying — some rule-verified zones' visit counts in that part of the range may be understated.`);
  }
  if (_result?.capHit) notes.push('<strong>Data volume cap reached</strong> in part of this range — some records may be missing.');

  banner.innerHTML = notes.join('<br>');
  banner.classList.toggle('hidden', !notes.length);
}

// ── Detail modal ──────────────────────────────────────────────────────────

function openDetail(zoneId: string, show = true): void {
  if (!_data) return;
  const zone = _data.zones.find(z => z.id === zoneId);
  if (!zone) return;
  _detailZoneId = zoneId;
  renderZoneDetail(
    zone,
    getLiveOccupancy()?.byZone.get(zoneId) ?? noOccupants,
    _result?.byZone.get(zoneId),
    _data,
    _detailSort,
    _innerSort,
  );
  const detailBody = el('zaDetailBody');
  if (detailBody) restoreExpanded(detailBody, _expandedVehicleRows, 'data-dev', 'data-visits');
  if (show) {
    el('zaDetailModal')?.classList.add('show');
    document.body.classList.add('vd-modal-open');
    el('zaDetailClose')?.focus();
  }
}

function closeDetail(): void {
  _detailZoneId = null;
  el('zaDetailModal')?.classList.remove('show');
  document.body.classList.remove('vd-modal-open');
}

// ── Analysis run ──────────────────────────────────────────────────────────

async function runAnalysis(): Promise<void> {
  if (_running || !_data) return;

  const fromEl = el('zaFrom') as HTMLInputElement;
  const toEl   = el('zaTo')   as HTMLInputElement;
  if (!fromEl.value || !toEl.value) { alert('Please choose a date range.'); return; }

  const from = new Date(fromEl.value + 'T00:00:00');
  const to   = new Date(toEl.value + 'T23:59:59');
  if (from >= to) { alert('The start date must be before the end date.'); return; }

  if (_scopeMode === 'vehicle' && !_focusDeviceId) { alert('Choose a vehicle first.'); return; }
  if (_scopeMode === 'driver' && !_focusDriverId) { alert('Choose a driver first.'); return; }

  const days    = Math.ceil((to.getTime() - from.getTime()) / 86_400_000);
  const maxDays = currentMaxDays();
  if (days > maxDays) {
    const scopeMsg = _scopeMode === 'group'
      ? `With ${scopedDeviceIds().length} vehicles in scope the maximum range is ${maxDays} days. Narrow the vehicle group or shorten the range.`
      : `The maximum range is ${maxDays} days. Shorten the range.`;
    alert(scopeMsg);
    return;
  }

  let deviceIds: string[];
  if (_scopeMode === 'group') {
    deviceIds = scopedDeviceIds();
    if (!deviceIds.length) { alert('No vehicles are in scope. Select at least one group.'); return; }
  } else if (_scopeMode === 'vehicle') {
    deviceIds = [_focusDeviceId!];
  } else {
    deviceIds = []; // resolved via driver discovery below, once the button/progress UI is up
  }

  const runBtn = el('zaRunBtn') as HTMLButtonElement;
  const label  = el('zaProgressLabel')!;
  _running = true;
  runBtn.disabled = true;
  const originalBtn = runBtn.innerHTML;
  runBtn.innerHTML = '<span style="opacity:0.6">Analysing…</span>';
  el('zaProgress')?.classList.remove('hidden');
  label.textContent = 'Starting…';
  _driverDiscoveryTruncated = false;

  const epoch = state.sessionEpoch;
  try {
    if (_scopeMode === 'driver') {
      const discovery = await discoverDriverDevices(
        from.toISOString(), to.toISOString(), _focusDriverId!,
        (msg) => { label.textContent = msg; },
      );
      if (epoch !== state.sessionEpoch) return;
      deviceIds = discovery.deviceIds;
      _driverDiscoveryTruncated = discovery.truncated;

      if (!deviceIds.length) {
        _result = null;
        _entityRows = [];
        renderEntityView();
        updateExportVisibility();
        updateMeta();
        const banner = el('zaWarnBanner')!;
        banner.innerHTML = `<strong>No trips found</strong> for ${escapeHtml(_focusDriverName ?? 'this driver')} in the selected range.`;
        banner.classList.remove('hidden');
        return;
      }
    }

    const result = await runZoneAnalysis({
      fromISO: from.toISOString(),
      toISO: to.toISOString(),
      deviceIds,
      onProgress: (msg) => { label.textContent = msg; },
    });
    if (epoch !== state.sessionEpoch) return;

    _result = result;

    if (_scopeMode === 'group') {
      renderGrid();
    } else {
      _entityRows = buildEntityZoneRows(result, _data!, _scopeMode === 'vehicle'
        ? (v) => v.deviceId === _focusDeviceId
        : (v) => v.driverId === _focusDriverId);
      renderEntityView();
    }
    updateExportVisibility();

    updateMeta();
    updateWarnBanner();
    if (_detailZoneId) openDetail(_detailZoneId, false);
  } catch (err) {
    if (epoch === state.sessionEpoch) {
      const banner = el('zaWarnBanner')!;
      banner.innerHTML = `<strong>Analysis failed</strong> — ${escapeHtml((err as Error).message)}`;
      banner.classList.remove('hidden');
    }
  } finally {
    if (epoch === state.sessionEpoch) {
      _running = false;
      runBtn.disabled = false;
      runBtn.innerHTML = originalBtn;
      el('zaProgress')?.classList.add('hidden');
    }
  }
}

// ── Lifecycle ─────────────────────────────────────────────────────────────

export function initZoneActivityPage(): void {
  wireOnce();
  if (_initialized || _loading) return;
  void loadPage();
}

async function loadPage(): Promise<void> {
  _loading = true;
  showZoneActivityState('loading', 'Loading zones…');
  const epoch = state.sessionEpoch;
  try {
    _data = await loadZoneActivityPageData();
    if (epoch !== state.sessionEpoch) return;

    if (!_data.zones.length) { showZoneActivityState('empty'); _initialized = true; return; }

    zaSetRange('7d');
    populateGroupSelector();
    populateVehiclePicker();
    populateDriverPicker();

    // Value stays the bare type name (that's what zone.typeNames matches on);
    // the count rides in the label so a zero-yield type is obvious before it's
    // picked rather than after it returns "No zones match". Grouping separates
    // this database's own types from the three Geotab ships with, which is
    // otherwise impossible to tell apart in a flat list.
    const typeSel = el('zaTypeFilter') as HTMLSelectElement;
    const optsFor = (list: ZoneTypeOption[]): string => list
      .map(t => `<option value="${escapeHtml(zoneTypeFilterValue(t.name))}">${escapeHtml(t.name)} (${t.count})</option>`)
      .join('');
    const custom  = _data.zoneTypes.filter(t => !t.isBuiltIn);
    const builtIn = _data.zoneTypes.filter(t => t.isBuiltIn);
    // "No type" is listed explicitly so the counts reconcile against the total
    // zone count — otherwise zones Geotab has left untyped are simply
    // unreachable from this filter and the numbers look like they don't add up.
    const untyped = _data.untypedZoneCount
      ? `<option value="${ZONE_TYPE_FILTER_UNTYPED}">No type assigned (${_data.untypedZoneCount})</option>`
      : '';
    typeSel.innerHTML = `<option value="">All types (${_data.zones.length})</option>`
      + (custom.length  ? `<optgroup label="Zone types">${optsFor(custom)}</optgroup>`  : '')
      + (builtIn.length ? `<optgroup label="Built-in">${optsFor(builtIn)}</optgroup>`   : '')
      + untyped;

    showZoneActivityState('results');
    updateMeta();
    updateWarnBanner();
    renderGrid();

    setOnLiveUpdate(applyLiveUpdate);
    await refreshLiveOccupancy();
    if (epoch !== state.sessionEpoch) return;
    startZoneActivityRefresh();
    _initialized = true;
  } catch (err) {
    if (epoch === state.sessionEpoch) {
      showZoneActivityState('error', `Error loading zones: ${(err as Error).message}`);
    }
  } finally {
    _loading = false;
  }
}

let _wired = false;

function wireOnce(): void {
  if (_wired) return;
  _wired = true;

  el('zaRunBtn')?.addEventListener('click', () => { void runAnalysis(); });

  el('zaExportBtn')?.addEventListener('click', () => {
    void (async () => {
      if (!_data) return;
      if (_scopeMode === 'group') {
        if (!_result) return;
        const { exportZoneActivityXlsx } = await import('./zone-activity-export');
        exportZoneActivityXlsx(_data, _result, getLiveOccupancy());
      } else {
        if (!_entityRows.length) return;
        const label = (_scopeMode === 'vehicle' ? _focusDeviceName : _focusDriverName) ?? 'Unknown';
        const { exportEntityZoneActivityXlsx } = await import('./zone-activity-export');
        exportEntityZoneActivityXlsx(
          label, _entityRows, _data, _result,
          _scopeMode === 'vehicle' ? 'Vehicle' : 'Driver',
        );
      }
    })();
  });

  // Quick ranges
  document.querySelectorAll<HTMLElement>('.za-qr-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.za-qr-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      zaSetRange(btn.dataset.range!);
    });
  });
  ['zaFrom', 'zaTo'].forEach(id => el(id)?.addEventListener('change', updateRangeCap));

  // Group selector
  el('zaGselectorToggle')?.addEventListener('click', (e) => {
    e.stopPropagation();
    el('zaGselectorPanel')?.classList.toggle('hidden');
  });
  el('zaGselectorPanel')?.addEventListener('click', (e) => e.stopPropagation());
  document.addEventListener('click', () => el('zaGselectorPanel')?.classList.add('hidden'));
  el('zaGselectAll')?.addEventListener('click', () => {
    el('zaGselectorList')?.querySelectorAll<HTMLInputElement>('input').forEach(cb => { cb.checked = true; });
    onScopeChanged();
  });
  el('zaGselectNone')?.addEventListener('click', () => {
    el('zaGselectorList')?.querySelectorAll<HTMLInputElement>('input').forEach(cb => { cb.checked = false; });
    onScopeChanged();
  });
  el('zaGsSearch')?.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase();
    el('zaGselectorList')?.querySelectorAll<HTMLElement>('.vselector-item').forEach(row => {
      const name = row.querySelector('.vselector-item-name')?.textContent?.toLowerCase() ?? '';
      row.style.display = name.includes(q) ? '' : 'none';
    });
  });

  // Scope mode toggle
  document.querySelectorAll<HTMLElement>('.za-scope-btn').forEach(btn => {
    btn.addEventListener('click', () => setScopeMode(btn.dataset.scope as ScopeMode));
  });

  // Vehicle picker (single-select combobox)
  el('zaVehiclePickerToggle')?.addEventListener('click', (e) => {
    e.stopPropagation();
    el('zaDriverPickerPanel')?.classList.add('hidden');
    el('zaVehiclePickerPanel')?.classList.toggle('hidden');
  });
  el('zaVehiclePickerPanel')?.addEventListener('click', (e) => e.stopPropagation());
  el('zaVehiclePickerSearch')?.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase();
    el('zaVehiclePickerList')?.querySelectorAll<HTMLElement>('.vselector-item-single').forEach(row => {
      row.style.display = (row.querySelector('.vselector-item-name')?.textContent ?? '').toLowerCase().includes(q) ? '' : 'none';
    });
  });

  // Driver picker (single-select combobox)
  el('zaDriverPickerToggle')?.addEventListener('click', (e) => {
    e.stopPropagation();
    el('zaVehiclePickerPanel')?.classList.add('hidden');
    el('zaDriverPickerPanel')?.classList.toggle('hidden');
  });
  el('zaDriverPickerPanel')?.addEventListener('click', (e) => e.stopPropagation());
  el('zaDriverPickerSearch')?.addEventListener('input', (e) => {
    const q = (e.target as HTMLInputElement).value.toLowerCase();
    el('zaDriverPickerList')?.querySelectorAll<HTMLElement>('.vselector-item-single').forEach(row => {
      row.style.display = (row.querySelector('.vselector-item-name')?.textContent ?? '').toLowerCase().includes(q) ? '' : 'none';
    });
  });

  document.addEventListener('click', () => {
    el('zaVehiclePickerPanel')?.classList.add('hidden');
    el('zaDriverPickerPanel')?.classList.add('hidden');
  });

  // Entity view: sortable header clicks (both the outer zone table and every
  // expanded entry/exit list) + expandable visit rows.
  el('zaEntityBody')?.addEventListener('click', (e) => {
    const target = e.target as Element;

    const eth = target.closest<HTMLElement>('th[data-esort]');
    if (eth) {
      const col = eth.dataset.esort as EntitySort['col'];
      _entitySort = _entitySort.col === col ? { col, asc: !_entitySort.asc } : { col, asc: col === 'zone' };
      renderEntityView();
      return;
    }

    const vth = target.closest<HTMLElement>('th[data-vsort]');
    if (vth) {
      const col = vth.dataset.vsort as VisitSort['col'];
      _innerSort = _innerSort.col === col ? { col, asc: !_innerSort.asc } : { col, asc: col === 'driver' };
      renderEntityView();
      return;
    }

    const row = target.closest<HTMLElement>('.za-vrow[data-ezone]');
    if (row) {
      const key = row.dataset.ezone!;
      const detail = row.parentElement?.querySelector<HTMLElement>(
        `.za-visits-row[data-evisits="${CSS.escape(key)}"]`,
      );
      if (detail) {
        detail.classList.toggle('hidden');
        const open = !detail.classList.contains('hidden');
        row.classList.toggle('is-open', open);
        if (open) _expandedZoneRows.add(key); else _expandedZoneRows.delete(key);
      }
    }
  });

  // Grid controls
  el('zaSearch')?.addEventListener('input', (e) => {
    _search = (e.target as HTMLInputElement).value;
    renderGrid();
  });
  el('zaTypeFilter')?.addEventListener('change', (e) => {
    _typeFilter = (e.target as HTMLSelectElement).value;
    renderGrid();
  });
  el('zaSortSel')?.addEventListener('change', (e) => {
    _sort = (e.target as HTMLSelectElement).value as typeof _sort;
    renderGrid();
  });
  el('zaActiveOnly')?.addEventListener('change', (e) => {
    _activeOnly = (e.target as HTMLInputElement).checked;
    renderGrid();
  });
  el('zaCompact')?.addEventListener('change', (e) => {
    const on = (e.target as HTMLInputElement).checked;
    el('zaCardGrid')?.classList.toggle('za-compact', on);
    localStorage.setItem('zaCompact', on ? '1' : '0');
  });
  if (localStorage.getItem('zaCompact') === '1') {
    (el('zaCompact') as HTMLInputElement).checked = true;
    el('zaCardGrid')?.classList.add('za-compact');
  }

  // Card interaction — delegated, with a roving-tabindex listbox.
  const grid = el('zaCardGrid');
  grid?.addEventListener('click', (e) => {
    const slot = (e.target as Element).closest<HTMLElement>('.za-slot[data-id]');
    if (slot?.dataset.id) openDetail(slot.dataset.id);
  });
  grid?.addEventListener('keydown', (e) => {
    const slot = (e.target as Element).closest<HTMLElement>('.za-slot[data-id]');
    if (!slot) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (slot.dataset.id) openDetail(slot.dataset.id);
      return;
    }
    const slots = [...grid.querySelectorAll<HTMLElement>('.za-slot')];
    const i = slots.indexOf(slot);
    let next = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = Math.min(slots.length - 1, i + 1);
    if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   next = Math.max(0, i - 1);
    if (e.key === 'Home') next = 0;
    if (e.key === 'End')  next = slots.length - 1;
    if (next >= 0 && next !== i) {
      e.preventDefault();
      slot.tabIndex = -1;
      slots[next].tabIndex = 0;
      slots[next].focus();
    }
  });

  // Detail modal
  el('zaDetailClose')?.addEventListener('click', closeDetail);
  el('zaDetailModal')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeDetail();
  });
  el('zaDetailBody')?.addEventListener('click', (e) => {
    const target = e.target as Element;

    const th = target.closest<HTMLElement>('th[data-sort]');
    if (th) {
      const col = th.dataset.sort as DetailSort['col'];
      _detailSort = _detailSort.col === col
        ? { col, asc: !_detailSort.asc }
        : { col, asc: col === 'name' };
      if (_detailZoneId) openDetail(_detailZoneId, false);
      return;
    }

    const vth = target.closest<HTMLElement>('th[data-vsort]');
    if (vth) {
      const col = vth.dataset.vsort as VisitSort['col'];
      _innerSort = _innerSort.col === col ? { col, asc: !_innerSort.asc } : { col, asc: col === 'driver' };
      if (_detailZoneId) openDetail(_detailZoneId, false);
      return;
    }

    const row = target.closest<HTMLElement>('.za-vrow[data-dev]');
    if (row) {
      const key = row.dataset.dev!;
      const detail = row.parentElement?.querySelector<HTMLElement>(
        `.za-visits-row[data-visits="${CSS.escape(key)}"]`,
      );
      if (detail) {
        detail.classList.toggle('hidden');
        const open = !detail.classList.contains('hidden');
        row.classList.toggle('is-open', open);
        if (open) _expandedVehicleRows.add(key); else _expandedVehicleRows.delete(key);
      }
    }
  });

  // How It Works
  el('zaHowBtn')?.addEventListener('click', () => el('zaHowModal')?.classList.add('show'));
  el('zaHowModalClose')?.addEventListener('click', () => el('zaHowModal')?.classList.remove('show'));
  el('zaHowModal')?.addEventListener('click', (e) => {
    if ((e.target as Element).id === 'zaHowModal') el('zaHowModal')?.classList.remove('show');
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (el('zaHowModal')?.classList.contains('show')) { el('zaHowModal')!.classList.remove('show'); return; }
    if (el('zaDetailModal')?.classList.contains('show')) closeDetail();
  });
}

export function resetZoneActivityPage(): void {
  stopZoneActivityRefresh();
  resetZoneActivityLive();
  resetZoneActivityData();
  // The next session may be a different user in a different zone.
  resetSessionTimeZone();
  disconnectPlateObserver();
  closeDetail();

  _data = null;
  _result = null;
  _initialized = false;
  _loading = false;
  _running = false;
  _search = '';
  _typeFilter = '';
  _sort = 'live';
  _activeOnly = false;
  _detailZoneId = null;
  _sigs.clear();
  _slots.clear();

  _scopeMode = 'group';
  _focusDeviceId = null;
  _focusDeviceName = null;
  _focusDriverId = null;
  _focusDriverName = null;
  _driverDiscoveryTruncated = false;
  _entityRows = [];
  _entitySort = { col: 'visits', asc: false };
  _innerSort = { col: 'entry', asc: false };
  _expandedVehicleRows.clear();
  _expandedZoneRows.clear();

  const searchEl = el('zaSearch') as HTMLInputElement | null;
  if (searchEl) searchEl.value = '';
  const activeEl = el('zaActiveOnly') as HTMLInputElement | null;
  if (activeEl) activeEl.checked = false;
  const grid = el('zaCardGrid');
  if (grid) grid.innerHTML = '';
  const exportBtn = el('zaExportBtn');
  if (exportBtn) exportBtn.style.display = 'none';
  el('zaWarnBanner')?.classList.add('hidden');

  document.querySelectorAll<HTMLElement>('.za-scope-btn').forEach(b => b.classList.toggle('active', b.dataset.scope === 'group'));
  el('zaGroupScopeWrap')  ?.classList.remove('hidden');
  el('zaVehicleScopeWrap')?.classList.add('hidden');
  el('zaDriverScopeWrap') ?.classList.add('hidden');
  el('zaZoneView')  ?.classList.remove('hidden');
  el('zaEntityView')?.classList.add('hidden');
  const vLabel = el('zaVehiclePickerLabel'); if (vLabel) vLabel.textContent = 'Choose a vehicle…';
  const dLabel = el('zaDriverPickerLabel');  if (dLabel) dLabel.textContent = 'Choose a driver…';
  const vList  = el('zaVehiclePickerList');  if (vList)  vList.innerHTML = '';
  const dList  = el('zaDriverPickerList');   if (dList)  dList.innerHTML = '';
  const eHeader = el('zaEntityHeader'); if (eHeader) eHeader.innerHTML = '';
  const eBody   = el('zaEntityBody');   if (eBody)   eBody.innerHTML = '';
  el('zaEntityLive')?.classList.add('hidden');

  showZoneActivityState('empty');
}
